import React, { useRef } from "react";
import { Box, OutlinedInput, TextField, Typography, useTheme } from "@mui/material";
import { useDarkTheme } from "../../utils/useDarkTheme";

export default function LoginOtpGroupedField({
  value,
  onChange,
  length = 6,
  error,
  helperText,
  primaryColor,
  fontFamily,
  onOtpInputStart,
  onOtpPaste,
  handleOtpKeyPress,
  customDetails
}) {
  const inputsRef = useRef([]);
  const theme = useTheme();
  const isDark = useDarkTheme(customDetails);

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/, ""); // only digits
    if (!val) return;
  
    const otpArray = value.split("").concat(Array(length).fill("")).slice(0, length);
  
    // find the first empty position from the start
    const firstEmptyIndex = otpArray.findIndex((digit) => !digit);
  
    // decide where to actually put the value
    const targetIndex =
      firstEmptyIndex !== -1 && index > firstEmptyIndex
        ? firstEmptyIndex
        : index;
  
    otpArray[targetIndex] = val[0];
    const newOtp = otpArray.join("");
    onChange(newOtp);
  
    // focus the next field
    if (targetIndex < length - 1) {
      inputsRef.current[targetIndex + 1]?.focus();
    }
  };
  

  const handleKeyDown = (e, index) => {
    const keysToPrevent = ["PageUp", "PageDown", "ArrowUp", "ArrowDown"];
    if (keysToPrevent.includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      const otpArray = value.split("");
  
      if (otpArray[index]) {
        // If current box has a value, clear it
        otpArray[index] = "";
        onChange(otpArray.join(""));
      } else if (index > 0) {
        // If empty, move focus to previous and clear that
        inputsRef.current[index - 1]?.focus();
        otpArray[index - 1] = "";
        onChange(otpArray.join(""));
      }
    }
  };

  const handleFocus = (e) => {
    onOtpInputStart?.("focus");

    e.target.addEventListener(
      "wheel",
      function (evt) {
        evt.preventDefault();
      },
      { passive: false }
    );
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    onChange(pasted);
    inputsRef.current[Math.min(pasted.length, length) - 1]?.focus();
    onOtpPaste?.(pasted);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ display: "flex", gap: "0.5rem" }}>
        {Array.from({ length }).map((_, idx) => (
          <TextField
            key={idx}
            value={value[idx] || ""}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onFocus={handleFocus}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              pattern: "[0-9]*",
              onKeyPress: handleOtpKeyPress,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px",
                },
                "& input": {
                  color: isDark ? "#FFFFFF" : "#000000",
                  textAlign: "center",
                  fontSize: "1.5rem",
                },
                "& input[type='text']": {
                  color: isDark ? "#FFFFFF" : "#000000",
                  WebkitTextFillColor: isDark ? "#FFFFFF" : "#000000",
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: "2px",
                },
                "&.Mui-error fieldset": {
                  borderColor: "error.main",
                },
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#FFFFFF" : "#000000",
                WebkitTextFillColor: isDark ? "#FFFFFF" : "#000000",
                caretColor: isDark ? "#FFFFFF" : "#000000",
                padding: {
                  xs: "16.5px 0",
                  md: "12.5px 0"
                }
              },
              borderRadius: "0px !important",
              fontFamily
            }}
            error={error}
            inputRef={(el) => (inputsRef.current[idx] = el)}
          />
        ))}
      </Box>
      {error && helperText && (
        <Typography
          color="error"
          fontSize="0.75rem"
          sx={{ padding: 0, mt: "4px", lineHeight: 1 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
